/**
 * Keystone scaling and affix multipliers (PLAN §6). Pure functions so the harness can tune them.
 */
import type { AffixId, Ms, NpcTier, ScalingMults } from '../contracts';

export interface KeystoneScalingTuning {
  /** Per-level multiplier up to and including `softCapLevel`. */
  perLevel: number;
  softCapLevel: number;
  /** Per-level multiplier past `softCapLevel`. */
  perLevelAfterCap: number;
}

/**
 * PLAN §6 starts from 1.08/level ("tunable; validate with the harness"). The first harness balance
 * pass (no gear progression in the sim) lowered it to 1.05 so a competent bot group stays near the
 * PLAN §1 pacing bands from +2 (~16.5 min, timed) to +8 (~22 min, depleted by a few minutes).
 */
export const DEFAULT_SCALING_TUNING: KeystoneScalingTuning = { perLevel: 1.05, softCapLevel: 10, perLevelAfterCap: 1.1 };

/**
 * Enemy HP and damage multiplier for a keystone level: perLevel^(L-1) up to +10, then ×1.10 per level.
 * Levels below 1 are treated as 1 (no scaling).
 */
export function keystoneMultiplier(level: number, tuning: KeystoneScalingTuning = DEFAULT_SCALING_TUNING): number {
  const l = Math.max(1, Math.floor(level));
  const capped = Math.min(l, tuning.softCapLevel);
  const over = Math.max(0, l - tuning.softCapLevel);
  return tuning.perLevel ** (capped - 1) * tuning.perLevelAfterCap ** over;
}

/**
 * Low-key enemy HP curve. The 1.05/level base scaling was tuned against high-key (+11) pacing, so
 * low keys were slogs (~48 s packs, ~113 s bosses at +2). This multiplies enemy **HP only**
 * (damage is untouched) and is exactly 1.0 from `fullLevel` up, so high keys and the progression
 * sweep's walls above +10 are unchanged.
 *
 * Shape, by key level L:
 *  - L ≤ `minLevel`: `minMult`.
 *  - `minLevel` < L ≤ `holdLevel`: the per-level key growth is cancelled, so enemy HP (key
 *    multiplier × this) stays at the `minLevel` value. Without this, Fortified at +4/+5 (+20% trash
 *    HP) pushes +5 packs past the low-key pack band.
 *  - `holdLevel` < L < `fullLevel`: eased ramp from the `holdLevel` value to 1.0.
 */
export interface LowKeyHpTuning {
  /** HP multiplier at `minLevel` (and below). */
  minMult: number;
  minLevel: number;
  /** Enemy HP stays flat from `minLevel` through this level (0 or ≤ minLevel = no hold). */
  holdLevel: number;
  /** First level with no reduction (multiplier 1.0). */
  fullLevel: number;
  /** Ramp shape: t (linear) or 3t²−2t³ (smoothstep), t = (L − holdLevel) / (fullLevel − holdLevel). */
  ease: 'linear' | 'smoothstep';
}

/**
 * Tuned with the harness (competent bots, venom_altar, 5 seeds): 0.5 at +2, enemy HP held flat
 * through +5, smoothstep to 1.0 at +10. Values: +2 0.50 · +3 0.476 · +4 0.454 · +5 0.432 ·
 * +6 0.491 · +7 0.632 · +8 0.800 · +9 0.941 · +10 and up 1.
 */
export const DEFAULT_LOW_KEY_HP: LowKeyHpTuning = { minMult: 0.5, minLevel: 2, holdLevel: 5, fullLevel: 10, ease: 'smoothstep' };

/** Enemy HP-only multiplier for low keys (see LowKeyHpTuning). 1.0 at `fullLevel` and above. */
export function lowKeyHpMultiplier(
  level: number,
  tuning: LowKeyHpTuning = DEFAULT_LOW_KEY_HP,
  scaling: KeystoneScalingTuning = DEFAULT_SCALING_TUNING,
): number {
  const l = Math.floor(level);
  if (l >= tuning.fullLevel) return 1;
  if (l <= tuning.minLevel) return tuning.minMult;
  const hold = Math.min(Math.max(tuning.holdLevel, tuning.minLevel), tuning.fullLevel);
  const held = (lv: number) => (tuning.minMult * keystoneMultiplier(tuning.minLevel, scaling)) / keystoneMultiplier(lv, scaling);
  if (l <= hold) return held(l);
  const from = held(hold);
  const t = (l - hold) / (tuning.fullLevel - hold);
  const e = tuning.ease === 'smoothstep' ? t * t * (3 - 2 * t) : t;
  return from + (1 - from) * e;
}

export interface AffixDef {
  /** Which NPCs the affix applies to. */
  appliesTo: 'trash' | 'boss';
  hp: number;
  damage: number;
}

/** Fortified: trash +20% HP, +30% damage. Tyrannical: bosses +30% HP, +15% damage. */
export const AFFIXES: Readonly<Record<string, AffixDef>> = {
  fortified: { appliesTo: 'trash', hp: 1.2, damage: 1.3 },
  tyrannical: { appliesTo: 'boss', hp: 1.3, damage: 1.15 },
};

/** Product of the affix multipliers that apply to an NPC of this tier. Unknown affixes are ignored. */
export function affixMultipliers(tier: NpcTier, affixes: readonly AffixId[]): ScalingMults {
  const kind = tier === 'boss' ? 'boss' : 'trash';
  let hp = 1;
  let damage = 1;
  for (const id of affixes) {
    const a = AFFIXES[id];
    if (!a || a.appliesTo !== kind) continue;
    hp *= a.hp;
    damage *= a.damage;
  }
  return { hp, damage };
}

/** Full scaling for an NPC tier at a key level with affixes (HP also gets the low-key curve). */
export function npcScaling(
  level: number,
  affixes: readonly AffixId[],
  tier: NpcTier,
  tuning?: KeystoneScalingTuning,
  lowKeyHp: LowKeyHpTuning = DEFAULT_LOW_KEY_HP,
): ScalingMults {
  const k = keystoneMultiplier(level, tuning);
  const a = affixMultipliers(tier, affixes);
  return { hp: k * lowKeyHpMultiplier(level, lowKeyHp, tuning) * a.hp, damage: k * a.damage };
}

/** Time added to the run timer per player death. */
export const DEATH_PENALTY_MS: Ms = 15_000;
/** Delay between a full-party wipe and the respawn at the entrance. */
export const WIPE_RESPAWN_DELAY_MS: Ms = 5_000;
/** Dead party members auto-release after the party has been out of combat this long. */
export const RELEASE_DELAY_MS: Ms = 5_000;
/** Party members this far from the entrance start the timer (the "start gate"). */
export const START_GATE_RADIUS = 10;

/** Chest thresholds as fractions of the timer: +3 at 60%, +2 at 80%, +1 at 100%. */
export const CHEST_THRESHOLDS: readonly { chest: 1 | 2 | 3; fraction: number }[] = [
  { chest: 3, fraction: 0.6 },
  { chest: 2, fraction: 0.8 },
  { chest: 1, fraction: 1 },
];

/** Keystone upgrade for a finished run (0 = depleted). */
export function chestFor(elapsedMs: Ms, timerMs: Ms): 0 | 1 | 2 | 3 {
  for (const t of CHEST_THRESHOLDS) if (elapsedMs <= timerMs * t.fraction) return t.chest;
  return 0;
}
